import React, { useEffect, useState } from "react";
import { api } from "../../api";
import { useAuth } from "../../AuthContext";
import { useHistoryNav } from "../../useHistoryNav";
import { useLanguage } from "../../i18n";
import { initials } from "../../format";
import { OwnerHome } from "./OwnerHome";
import { OwnerListings } from "./OwnerListings";
import { OwnerLedgers } from "./OwnerLedgers";
import { OwnerRequests } from "./OwnerRequests";
import { OwnerSettings } from "./OwnerSettings";
import { OwnerReports } from "./OwnerReports";
import { OwnerPlans } from "./OwnerPlans";
import { ReferEarn } from "../shared/ReferEarn";
import { OwnerBroadcast } from "./OwnerBroadcast";
import { ProfileScreen } from "../shared/ProfileScreen";
import { NotificationBell, NotificationsScreen } from "../shared/Notifications";
import { useAutoRefresh } from "../../useAutoRefresh";
import { UpdateNotice, RatingPrompt } from "../shared/AppPrompts";
import { VerifyEmailNotice } from "../shared/VerifyEmail";
import { FoundingOwnerNotice } from "../shared/FoundingOffer";
import { ConfirmLocationNotice } from "./ConfirmLocationNotice";
import { useAutoEnablePush } from "../shared/NotificationSettings";
import { PhotoNudgePopup } from "./PhotoNudgePopup";
import { HomeIcon, BuildingIcon, NotebookIcon, WrenchIcon, SettingsIcon } from "../shared/Icons";
import { trackScreen } from "../../analytics";
import { usePullToRefresh } from "../../usePullToRefresh";
import { navTapFx } from "../shared/navRipple";
import { useConsumePendingNav } from "../../pendingNav";
import { useLiveNotifications } from "../../liveNotifications";

type View = "home" | "listings" | "ledgers" | "reports" | "requests" | "settings" | "profile" | "notifications" | "plans" | "refer" | "broadcast";

export function OwnerApp() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const NAV: { key: View; label: string; icon: React.ReactNode }[] = [
    { key: "home", label: t("nav.home"), icon: <HomeIcon /> },
    { key: "listings", label: t("nav.listings"), icon: <BuildingIcon /> },
    { key: "ledgers", label: t("nav.notebook"), icon: <NotebookIcon /> },
    { key: "requests", label: t("nav.requests"), icon: <WrenchIcon /> },
    { key: "settings", label: t("nav.settings"), icon: <SettingsIcon /> },
  ];
  const [{ view, focusPropertyId, focusTab }, pushNav] = useHistoryNav<{ view: View; focusPropertyId: string | null; focusTab: string | null }>({
    view: "home",
    focusPropertyId: null,
    focusTab: null,
  });
  const [pendingVisits, setPendingVisits] = useState(0);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  // Bumped on pull-to-refresh and used as the active view's key, so
  // dragging down forces a full remount of whatever screen is showing —
  // re-running its own data-fetch effects, the same as navigating to it fresh.
  const [refreshKey, setRefreshKey] = useState(0);
  useAutoEnablePush();

  async function refreshPendingVisits() {
    const { visits } = await api.get("/api/visits/owner");
    setPendingVisits(visits.filter((v: any) => v.status === "pending").length);
  }

  async function refreshUnreadNotifications() {
    const { unreadCount } = await api.get("/api/notifications/mine");
    setUnreadNotifications(unreadCount);
  }
  useLiveNotifications(setUnreadNotifications);

  const { ref: contentRef, pull, refreshing, triggerPx } = usePullToRefresh<HTMLDivElement>(async () => {
    await Promise.all([refreshPendingVisits(), refreshUnreadNotifications()]);
    setRefreshKey((k) => k + 1);
  });

  useAutoRefresh(() => {
    refreshPendingVisits();
    refreshUnreadNotifications();
    setRefreshKey((k) => k + 1);
  });

  useEffect(() => {
    refreshPendingVisits();
    refreshUnreadNotifications();
    trackScreen(`owner/${view}`);
  }, [view]);

  function navigate(v: View, propertyId?: string, tab?: string) {
    pushNav({ view: v, focusPropertyId: propertyId ?? null, focusTab: tab ?? null });
  }
  useConsumePendingNav(navigate);

  return (
    <>
      <div className="app-header">
        <div className="wordmark-row">
          <img src="/logo.svg" alt="" className="wordmark-logo" />
          <div className="wordmark">
            <b>ToLetBook</b>
            <span>{t("nav.ownerDashboard")}</span>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <NotificationBell unreadCount={unreadNotifications} onClick={() => navigate("notifications")} />
          <button className="avatar-sm" style={{ border: "none", padding: 0, cursor: "pointer" }} onClick={() => navigate("profile")} aria-label={t("settings.profileMenu")} title={t("settings.profileMenu")}>
            {user ? initials(user.name) : ""}
          </button>
        </div>
      </div>

      <div className="content" ref={contentRef}>
        <div className="ptr-indicator" style={{ height: refreshing ? 40 : pull }}>
          {refreshing ? (
            <div className="ptr-spinner" />
          ) : (
            <svg
              className={`ptr-arrow${pull >= triggerPx ? " ready" : ""}`}
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 5v14M6 13l6 6 6-6" />
            </svg>
          )}
        </div>
        <div className="greet">
          {t("common.welcomeBackGreet")} <b>{user?.name.split(" ")[0]}</b>
        </div>
        <FoundingOwnerNotice />
        <ConfirmLocationNotice />
        <VerifyEmailNotice />
        <UpdateNotice />
        <RatingPrompt />
        <div className="view" key={refreshKey}>
          {view === "home" && <OwnerHome onNavigate={navigate} />}
          {view === "listings" && <OwnerListings focusPropertyId={focusPropertyId} onNavigate={navigate} />}
          {view === "ledgers" && <OwnerLedgers />}
          {view === "reports" && <OwnerReports onBack={() => window.history.back()} />}
          {view === "requests" && <OwnerRequests focusTab={focusTab} onVisitsChanged={refreshPendingVisits} />}
          {view === "settings" && <OwnerSettings onNavigate={navigate} />}
          {view === "plans" && <OwnerPlans onBack={() => window.history.back()} />}
          {view === "refer" && <ReferEarn onBack={() => window.history.back()} />}
          {view === "broadcast" && <OwnerBroadcast onBack={() => window.history.back()} />}
          {view === "profile" && <ProfileScreen onBack={() => window.history.back()} />}
          {view === "notifications" && (
            <NotificationsScreen
              onBack={() => window.history.back()}
              onChanged={refreshUnreadNotifications}
              onNavigate={(v, tab) => navigate(v as View, undefined, tab)}
            />
          )}
        </div>
      </div>

      <nav className="bottomnav">
        {NAV.map((n) => (
          <button
            key={n.key}
            className={`nav-btn${view === n.key ? " active" : ""}`}
            onClick={(e) => {
              navTapFx(e);
              navigate(n.key);
            }}
          >
            <span className="nav-icon-wrap">
              {n.icon}
              {n.key === "requests" && pendingVisits > 0 && <span className="nav-badge" />}
            </span>
            <span>{n.label}</span>
          </button>
        ))}
      </nav>

      <PhotoNudgePopup onAddPhotos={(propertyId) => navigate("listings", propertyId)} />
    </>
  );
}
